import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { useTenantContext } from '@/lib/hooks/use-tenant-context';
import { useAuth } from '@/features/auth/auth-provider';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/shared/empty-state';
import { ROUTES } from '@/app/routes';
import { useDocumentTitle } from '@/lib/hooks/use-document-title';
import { useGetApiAgents } from '@/lib/api/generated/agents/agents';
import {
  deleteApiLightweightAutomationsAutomationId,
  getApiLightweightAutomationsAutomationId,
  postApiLightweightAutomations,
  postApiLightweightAutomationRuns,
  usePostApiLightweightAutomationRunNow,
} from '@/lib/api/generated/automations/automations';
import { useTenantDirectoryUsersQuery } from '@/lib/query/tenant-app-users';
import { queryKeys } from '@/lib/query/keys';
import { toastMutationError } from '@/lib/utils/formatting';
import { type LightweightAutomationRecord } from './automation-domain';
import { AutomationForm, toFormValues } from './automation-form';

export function EditAutomationPage() {
  const { automationId } = useParams<{ automationId: string }>();
  const { tenantId: activeTenantId } = useTenantContext();
  const { user, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: automation, isLoading } = useQuery({
    queryKey: queryKeys.automation(activeTenantId, automationId!),
    queryFn: () => getApiLightweightAutomationsAutomationId(automationId!, { tenantId: activeTenantId }) as Promise<LightweightAutomationRecord>,
    enabled: Boolean(activeTenantId && automationId),
  });
  const tenantScopedQueryOptions = { query: { enabled: Boolean(activeTenantId) } };
  const { data: agents, isLoading: agentsLoading } = useGetApiAgents(tenantScopedQueryOptions);
  const directoryUsersQuery = useTenantDirectoryUsersQuery(activeTenantId);
  const { data: runs = [] } = useQuery({
    queryKey: queryKeys.automationRuns(activeTenantId, automationId!),
    queryFn: () =>
      postApiLightweightAutomationRuns({
        tenantId: activeTenantId,
        automationId: automationId!,
      }),
    enabled: Boolean(activeTenantId && automationId),
  });
  const saveMutation = useMutation({
    mutationFn: (record: LightweightAutomationRecord) =>
      postApiLightweightAutomations(
        { record: record as Record<string, unknown> },
        { headers: { "x-tenant-id": record.tenantId } },
      ),
    onSuccess: async (_record, input) => {
      await queryClient.invalidateQueries({ queryKey: ['/api/lightweight-automations', { tenantId: input.tenantId }] });
      await queryClient.invalidateQueries({ queryKey: [`/api/lightweight-automations/${input.automationId}`, { tenantId: input.tenantId }] });
      await queryClient.invalidateQueries({ queryKey: queryKeys.automationRuns(input.tenantId, input.automationId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.state });
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (input: { tenantId: string; automationId: string }) =>
      deleteApiLightweightAutomationsAutomationId(input.automationId, {}, { tenantId: input.tenantId }),
    onSuccess: async (_record, input) => {
      await queryClient.invalidateQueries({ queryKey: ['/api/lightweight-automations', { tenantId: input.tenantId }] });
      await queryClient.invalidateQueries({ queryKey: [`/api/lightweight-automations/${input.automationId}`, { tenantId: input.tenantId }] });
      await queryClient.invalidateQueries({ queryKey: queryKeys.state });
    },
  });
  const runMutation = usePostApiLightweightAutomationRunNow({
    mutation: {
      onSuccess: async (_result, variables) => {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.automationRuns(activeTenantId, variables.data.automationId),
        });
        await queryClient.invalidateQueries({ queryKey: ['/api/lightweight-automations', { tenantId: activeTenantId }] });
        await queryClient.invalidateQueries({ queryKey: queryKeys.state });
      },
    },
  });
  useDocumentTitle(automation?.name ?? 'Edit Automation');

  const formInitialValues = useMemo(() => (automation ? toFormValues(automation) : undefined), [automation]);
  const currentDirectoryUser = useMemo(
    () => directoryUsersQuery.data?.items.find((entry) => entry.id === user?.actorId) ?? null,
    [directoryUsersQuery.data?.items, user?.actorId],
  );
  const canAuthorShared = Boolean(
    isPlatformAdmin || currentDirectoryUser?.roleKeys.includes('agentiq_admin'),
  );

  const handleSubmit = async (record: LightweightAutomationRecord) => {
    try {
      await saveMutation.mutateAsync(record);
      toast.success('Automation updated');
      navigate(ROUTES.tenantAutomations(tenantId!));
    } catch (err) {
      toast.error(toastMutationError(err, 'Failed to update automation'));
    }
  };

  const handleRunNow = async () => {
    try {
      await runMutation.mutateAsync({
        data: { tenantId: activeTenantId, automationId: automationId! },
      });
      toast.success('Automation queued');
    } catch (err) {
      toast.error(toastMutationError(err, 'Failed to run automation'));
    }
  };

  if (isLoading || agentsLoading) {
    return <EmptyState title="Loading automation" description="Fetching the automation definition, agents, and recent runs." />;
  }

  if (!automation || !agents) {
    return <EmptyState title="Automation not found" description="The automation may have been deleted or you may not have access to it." />;
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader className="gap-2">
          <CardTitle>Edit Automation</CardTitle>
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            Update the instructions, delivery target, and schedule without dropping into raw workflow JSON.
          </p>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-mono" style={{ color: 'var(--muted-foreground)' }}>{automation.automationId}</span>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Input: {automation.inputSource.kind}</span>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Output: {automation.delivery.mode}</span>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Last run: {automation.lastRunAt ?? 'none'}</span>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Next run: {automation.nextRunAt ?? 'none'}</span>
          <div className="ml-auto flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => void handleRunNow()}>
              Run now
            </Button>
          </div>
        </CardContent>
      </Card>

      <AutomationForm
        tenantId={activeTenantId}
        title={automation.name}
        submitLabel="Save automation"
        agents={agents}
        tenantUsers={directoryUsersQuery.data?.items ?? []}
        currentUserId={user?.actorId ?? ''}
        canAuthorShared={canAuthorShared}
        initialValues={formInitialValues}
        onSubmit={handleSubmit}
        onDelete={async () => {
          try {
            await deleteMutation.mutateAsync({ tenantId: activeTenantId, automationId: automation.automationId });
            toast.success('Automation deleted');
            navigate(ROUTES.tenantAutomations(activeTenantId));
          } catch (err) {
            toast.error(toastMutationError(err, 'Failed to delete automation'));
          }
        }}
        onCancel={() => navigate(ROUTES.tenantAutomations(activeTenantId))}
      />

      <Card>
        <CardHeader className="gap-2">
          <CardTitle>Recent Runs</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {runs.length === 0 ? (
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>No runs yet.</p>
          ) : (
            runs.slice(0, 10).map((run) => (
              <div
                key={run.runId}
                className="flex flex-col gap-1 rounded-[var(--radius-sm)] border border-border p-3"
                style={{ background: 'var(--bg-elevated)' }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-mono" style={{ color: 'var(--muted-foreground)' }}>{run.runId}</span>
                  <span className="text-xs" style={{ color: 'var(--text-strong)' }}>{run.status}</span>
                </div>
                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Requested: {run.requestedAt}</span>
                {run.summary ? <span className="text-xs" style={{ color: 'var(--text-strong)' }}>{run.summary}</span> : null}
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
